import { FLOTABOT } from "../components/layout/secciones";
import { abrirSesionEnFlotaBot } from "./ssoFlotaBot";

/* Subir la planilla de movimientos desde el hub.
 *
 * ── POR QUÉ EL TRABAJO LO HACE FLOTABOT ───────────────────────────────────
 *
 * El hub es React estático: no tiene servidor. El lector de estas planillas es
 * `planilla_movimientos.py` —ochocientas líneas que entienden los dos bloques
 * de cada hoja, los choferes con sufijo de empresa, los fleteros, los furgones
 * múltiples y los destinos escritos de diez formas—, y reescribirlo en
 * JavaScript sería una segunda copia de la parte más delicada del sistema.
 *
 * Así que la pantalla vive acá, que es donde Nico quiere que la gente trabaje,
 * y el archivo viaja a FlotaBot por el mismo puente que ya usa el botón de
 * «Sincronizar documentos».
 *
 * ── DOS PASOS, Y EL ARCHIVO VIAJA EN LOS DOS ──────────────────────────────
 *
 * `analizar` mira y contesta qué haría. `aplicar` escribe lo confirmado. Entre
 * los dos no queda nada guardado en el servidor: dos personas pueden subir a
 * la vez sin pisarse, y un worker reciclado en el medio no deja un archivo
 * huérfano esperando una confirmación que nunca llega.
 *
 * Y en el segundo paso se mandan DECISIONES, nunca filas: claves e ids. Los
 * valores los relee el servidor del Excel. Mandar las filas ya armadas sería
 * más corto y permitiría inventar un viaje que la planilla no dice.
 */

/* La respuesta tiene siempre la misma forma, salga bien o mal:
 *
 *   { ok, mensaje, ...lo que devuelva la ruta }
 *
 * `ok:false` con `mensaje` es lo que la pantalla muestra. Es la misma forma que
 * `sincronizarDocs`, a propósito: dos formas distintas para el mismo tipo de
 * llamada terminan en un `if` distinto en cada botón. */
/* Exportada porque la usa también `planillaCargas`: son dos planillas y UN
 * puente. Con dos copias de esto, el día que cambie el manejo de sesión una
 * pantalla quedaría contestando distinto que la otra. */
export async function llamar(ruta, cuerpo, queNoPuedo) {
  const sesion = await abrirSesionEnFlotaBot();
  if (!sesion.ok) {
    return {
      ok: false,
      mensaje:
        sesion.motivo === "sin-sesion"
          ? "Se cerró la sesión. Entrá de nuevo y probá otra vez."
          : "No se pudo hablar con FlotaBot. Puede estar reiniciando; probá en un minuto.",
    };
  }

  let r;
  try {
    r = await fetch(`${FLOTABOT}${ruta}`, {
      method: "POST",
      credentials: "include",
      body: cuerpo,
    });
  } catch (e) {
    /* NO TODO LO QUE CAE ACÁ ES LA RED, y tratarlo como si lo fuera costó una
     * tarde. Aldo, 29/09: apretaba Cargar y «no pasaba nada». El pedido nunca
     * llegó al servidor —no hay rastro— porque lo que falló fue LEER el
     * archivo del disco para armar el cuerpo: entre el análisis y el clic el
     * Excel había cambiado. El cartel decía «no se pudo conectar», así que
     * mandó a mirar la red, que estaba perfecta.
     *
     * El archivo ahora se retiene en memoria (ver `archivoRetenido`), así que
     * este caso no debería volver por ese camino. La distinción queda igual:
     * si vuelve por otro, que lo diga en vez de disfrazarse de red. */
    const porElArchivo =
      e && ["NotReadableError", "NotFoundError", "SecurityError",
            "ArchivoIlegible"].includes(e.name);
    if (porElArchivo) {
      return {
        ok: false,
        mensaje:
          "No pude leer el archivo para mandarlo. Suele pasar cuando el Excel "
          + "se abrió, se guardó o se movió después de elegirlo. Volvé a "
          + "elegirlo y probá de nuevo.",
      };
    }
    /* Red o CORS. Para quien sube el archivo son lo mismo —no pasó nada— y
     * decirle «error de CORS» lo manda a buscar donde no puede arreglarlo. */
    return { ok: false, mensaje: "No se pudo conectar con FlotaBot." };
  }

  /* «No podés» y «no pude» no son lo mismo. Un 403 es el permiso; cualquier
   * otra cosa es una falla, y mandar a pedir un permiso por un servidor caído
   * hace buscar el problema donde no está. */
  if (r.status === 403) return { ok: false, mensaje: queNoPuedo };
  if (r.status === 401) {
    return {
      ok: false,
      mensaje: "FlotaBot no reconoció la sesión. Recargá la página y probá otra vez.",
    };
  }

  let datos = null;
  try {
    datos = await r.json();
  } catch {
    datos = null;
  }
  if (!datos) return { ok: false, mensaje: `FlotaBot contestó ${r.status}.` };
  /* El 400 trae el motivo adentro y ese motivo está escrito para leerse —«tiene
   * que ser un .xlsx»—, así que se muestra tal cual en vez de taparlo con un
   * genérico. */
  if (!r.ok) return { ok: false, mensaje: datos.error || `FlotaBot contestó ${r.status}.` };
  return datos;
}

const NO_PUEDO = "No podés pasar.";

/* Paso 1: qué haría con este archivo. No escribe nada. */
export async function analizarPlanilla(archivo) {
  const fd = new FormData();
  fd.append("archivo", archivo);
  return llamar("/api/planilla/analizar", fd, NO_PUEDO);
}

/* Paso 2: cargar lo confirmado.
 *
 * `nuevas` son claves; `correcciones` y `descartes` son `{clave, id}`. El
 * archivo va otra vez porque el servidor lo relee — ver el encabezado.
 * `descartes` son las cuasi-duplicadas que se decidió DEJAR como están: no
 * cargan nada, se recuerdan para no volver a ofrecerlas. `apartes` son las
 * que se decidió cargar como OTRO viaje, también `{clave, id}`: el id es el
 * del viaje contra el que se dijo «es otro» (ticket #403).
 */
export async function aplicarPlanilla(
  archivo,
  nuevas,
  correcciones,
  descartes,
  fechasX,
  rellenos,
  apartes,
) {
  const fd = new FormData();
  fd.append("archivo", archivo);
  fd.append("nuevas", JSON.stringify(nuevas || []));
  fd.append("correcciones", JSON.stringify(correcciones || []));
  fd.append("descartes", JSON.stringify(descartes || []));
  /* Días tipeados a mano para las celdas con «X». Van como decisiones, igual
   * que el resto: el servidor los aplica SÓLO donde relee una «X». */
  fd.append("fechas_x", JSON.stringify(fechasX || []));
  /* Rellenos (Igna, 25/09): completar una salida/llegada que estaba en blanco
   * no pisa nada, así que van SIN pedir el clic —los manda el análisis y se
   * aplican solos—. Como todo, viajan como decisión {clave, id} y el servidor
   * relee el valor del archivo. */
  fd.append("rellenos", JSON.stringify(rellenos || []));
  /* «Es otro viaje — cargarlo aparte»: sin el id del otro viaje, FlotaBot no
   * puede distinguir la fila de un duplicado de ése y no la carga. */
  fd.append("apartes", JSON.stringify(apartes || []));
  return llamar("/api/planilla/aplicar", fd, NO_PUEDO);
}
